/**
 * Testing the performance using SVGs rather than canvas
 */

import type { RendererLine, RendererRect } from "./canvas"
import SvgSvelteRenderer__SvelteComponent_ from "@components/svg-renderer/SVGSvelteRenderer.svelte"

export type SVGProperties = {
    stroke: string
    class: string
    opacity: string
    width: string
    height: string
    "stroke-width": string
    fill: string
    "paint-order": "normal" | "fill" | "stroke" | "markers"
    "stroke-linejoin": "bevel" | "miter" | "round"
    "stroke-linecap": "butt" | "round" | "square"
    x: string
    y: string
    rx: string
    ry: string
    x1: string
    x2: string
    y1: string
    y2: string
}

/**
 * Remember to call {@link SVGRenderer.show} whenever the data is ready
 */
export class SVGRenderer {
    /**
     * Multiplies the resolution
     */
    // static SHOW_ANIMATION = "canvas--fadeIn 0.5s ease-out forwards"
    // static HIDE_ANIMATION = "canvas--fadeOut 0s ease-out forwards"

    // #svg: SVGElement
    #lines: Partial<SVGProperties>[] = []
    #rects: Partial<SVGProperties>[] = []
    #reactive: SVGRendererReact

    /**
     * Whether or not the Canvas is opacified
     */
    #showing = false
    get showing() {
        return this.#showing
    }
    constructor(public readonly parent: HTMLElement) {
        // this.#svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
        // console.log("SVG ELEMENT", this.#svg)
        // this.#svg.style.position = "absolute"
        // // this.#svg.style.willChange = "transform"
        // // this.#canvas.style.opacity = "100%"
        // this.#svg.style.top = "0"
        // this.#svg.style.left = "0"
        // this.#svg.style.zIndex = "0"
        // this.resizeCanvas()
        // this.#svg.style.pointerEvents = "none"
        // this.parent.appendChild(this.#svg)

        this.#reactive = new SVGRendererReact(parent)
    }

    /**
     * Use when postponing re-renders so there aren't weird
     * highlights on the screen
     */
    hide() {
        if (this.#showing === false) return
        this.#showing = false
        this.#reactive.hide()
    }

    show() {
        if (this.#showing === true) return
        this.#reactive.show()
        this.#showing = true
    }

    resizeCanvas() {
        // console.log("Resizing...")
        this.#reactive.resize(this.parent.offsetWidth, this.parent.offsetHeight)
        // this.#svg.style.width = this.parent.offsetWidth + "px"
        // this.#svg.style.height = this.parent.offsetHeight + "px"
        // this.#svg.setAttribute(
        //     "viewbox",
        //     `0 0 ${this.parent.offsetWidth} ${this.parent.offsetHeight}`
        // )
    }

    clear() {
        // this.#svg.innerHTML = ""
        this.#lines = []
        this.#rects = []
    }

    draw() {
        this.#reactive.redraw(this.#lines, this.#rects)
    }

    static ROUNDED_CORNER = 5
    static STROKE_PADDING = 1

    drawLine(lines: RendererLine[], properties: Partial<SVGProperties> = {}) {
        lines.forEach((line) => this.appendLine(line, properties))
    }

    appendLine(line: RendererLine, properties: Partial<SVGProperties> = {}) {
        // const svgns = "http://www.w3.org/2000/svg" // SVG namespace
        // const svgLine = document.createElementNS(svgns, "line") // Create SVG line

        const svgLine: Partial<SVGProperties> = {
            x1: line.from.x.toString(),
            x2: line.to.x.toString(),
            y1: line.from.y.toString(),
            y2: line.to.y.toString(),
            ...properties,
        }

        this.#lines.push(svgLine)

        // // Set attributes of the line
        // svgLine.setAttributeNS(null, "x1", line.from.x.toString())
        // svgLine.setAttributeNS(null, "y1", line.from.y.toString())
        // svgLine.setAttributeNS(null, "x2", line.to.x.toString())
        // svgLine.setAttributeNS(null, "y2", line.to.y.toString())

        // Object.entries(properties).forEach(([key, value]) => {
        //     svgLine.setAttributeNS(null, key, value)
        // })

        // // Append the line to SVG
        // this.#svg.appendChild(svgLine)
    }

    appendRect(rect: RendererRect, properties: Partial<SVGProperties> = {}) {
        const svgns = "http://www.w3.org/2000/svg" // SVG namespace
        // const svgRectangle = document.createElementNS(svgns, "rect") // Create SVG rect

        // Set attributes of the rect

        const svgRect: Partial<SVGProperties> = {
            x: rect.x.toString(),
            y: rect.y.toString(),
            width: rect.width.toString(),
            height: rect.height.toString(),
            rx: SVGRenderer.ROUNDED_CORNER + "",
            ry: SVGRenderer.ROUNDED_CORNER + "",
            ...properties,
        }

        this.#rects.push(svgRect)

        // // Set attributes of the rect
        // svgRectangle.setAttributeNS(null, "x", rect.x.toString())
        // svgRectangle.setAttributeNS(null, "y", rect.y.toString())
        // svgRectangle.setAttributeNS(null, "width", rect.width.toString())
        // svgRectangle.setAttributeNS(null, "height", rect.height.toString())
        // svgRectangle.setAttributeNS(null, "rx", SVGRenderer.ROUNDED_CORNER + "")
        // svgRectangle.setAttributeNS(null, "ry", SVGRenderer.ROUNDED_CORNER + "")
        // Object.entries(properties).forEach(([key, value]) => {
        //     svgRectangle.setAttributeNS(null, key, value)
        // })

        // Append the rect to SVG
        // this.#svg.appendChild(svgRectangle)
    }

    drawRectangles(rectangles: RendererRect[], properties: Partial<SVGProperties> = {}) {
        rectangles.forEach((rect) => this.appendRect(rect, properties))
    }

    destroy() {
        this.#reactive.destroy()
        // this.#svg.remve
    }
}

class SVGRendererReact {
    #component: SvgSvelteRenderer__SvelteComponent_
    constructor(parent: HTMLElement) {
        this.#component = new SvgSvelteRenderer__SvelteComponent_({
            target: parent,
        })
    }

    show() {
        this.#component.$set({ showing: true })
    }

    hide() {
        this.#component.$set({ showing: false })
    }

    resize(width: number, height: number) {
        this.#component.$set({
            width,
            height,
        })
    }

    redraw(lines: Partial<SVGProperties>[], rects: Partial<SVGProperties>[]) {
        this.#component.$set({
            rects,
            lines,
        })
    }

    destroy() {
        this.#component.$destroy()
    }
}
